import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Space, Tooltip, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Table from "./Table";

import { all as AllSurgicalIntervention } from "../../api/SurgicalIntervention";
import { all as AllCareUnit } from "../../api/CareUnit";

const { Option } = Select;

const layoutSelect = {
  showSearch: true,
  allowClear: true,
  defaultActiveFirstOption: true,
  optionFilterProp: "children",
  style: {
    width: 400,
  },
};

const DEFAULT_SORTER = {
  column: {
    dataIndex: "expected_end_at",
  },
  columnKey: undefined,
  field: "expected_end_at",
  order: "ascend",
};

/**
 *
 */
const SurgicalInterventionTable = forwardRef((props, ref) => {
  const { mode, surgicalInterventionIds } = props;

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [sorter, setSorter] = useState({ ...DEFAULT_SORTER });
  const [surgicalInterventions, setSurgicalInterventions] = useState([]);
  const [careUnits, setCareUnits] = useState([]);
  const [selectedCareUnitIds, setSelectedCareUnitIds] = useState([]);
  const [showSearchInfo, setShowSearchInfo] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  let timeoutFilter = null;

  /**
   *
   * @param {*} pagination
   * @param {*} filters
   * @param {*} sorter
   * @returns
   */
  const getPayload = (pagination, filters, sorter) => {
    let payload = {
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    if (searchValue || selectedCareUnitIds.length > 0 || surgicalInterventionIds) {
      payload["q"] = {};

      if (searchValue) {
        payload.q["search"] = searchValue;
      }

      if (selectedCareUnitIds.length > 0) {
        payload.q["care_unit_ids"] = selectedCareUnitIds;
      }

      if (surgicalInterventionIds) {
        payload.q["_id"] = surgicalInterventionIds;
      }
    }

    if (sorter.column === undefined) {
      sorter = { ...DEFAULT_SORTER };
    }

    let { order, column } = sorter;
    let field = column.columnKey ? column.columnKey : column.dataIndex;
    order = order === "ascend" ? 1 : -1;

    if (!!field && !!order) {
      payload["sort"] = field + "," + order;
    }

    return payload;
  };

  /**
   *
   * @param {*} pagination
   * @param {*} filters
   * @param {*} sorter
   */
  const fetch = async function (pagination, filters, sorter) {
    setLoading(true);

    let payload = getPayload(pagination, filters, sorter);

    return AllSurgicalIntervention(payload).then((data) => {
      setSurgicalInterventions(data.results);
      setPagination({
        ...pagination,
        total: data.total,
      });
      setSorter({ ...sorter });
      setLoading(false);
    });
  };

  /**
   *
   * @param {*} search
   */
  const fetchCareUnits = async function (search) {
    return AllCareUnit({
      current: 1,
      pageSize: 10,
      q: {
        search,
      },
    }).then((data) => {
      setCareUnits(data.results);
    });
  };

  /**
   *
   * @param {*} value
   */
  const onChangeCareUnits = function (value) {
    setSelectedCareUnitIds(value);
  };

  /**
   *
   * @param {*} e
   */
  const handleSearchValue = (e) => {
    let value = e.target.value;

    clearTimeout(timeoutFilter);

    let searchLength = value.length;
    let searchValid = searchLength === 0 || searchLength > 2;

    timeoutFilter = setTimeout(() => {
      setShowSearchInfo(!searchValid);

      if (searchValid) {
        setSearchValue(e.target.value);
      }
    }, 500);

  };

  useEffect(async () => {
    fetchCareUnits("");
  }, []);

  useEffect(() => {
    fetch(
      { ...pagination, current: 1 },
      null,
      sorter
    );
  }, [selectedCareUnitIds, surgicalInterventionIds, searchValue]);

  useImperativeHandle(ref, () => ({
    fetch: () => {
      fetch(pagination, null, sorter);
    },
  }));

  let careUnitOptions = careUnits.map((option, index) => (
    <Option key={index} value={option.id}>
      {option.name}
    </Option>
  ));

  return (
    <div>
      <Space className="mb-4" direction="horizontal" align="start">
        {mode !== "out" ? (
          <Space direction="vertical">
            <label>Unité(s) de soin(s) :</label>
            <Select
              {...layoutSelect}
              mode="multiple"
              placeholder="Choisissez votre/vos unité(s)"
              onChange={onChangeCareUnits}
              notFoundContent="Rechercher une unité de soin"
            >
              {careUnitOptions}
            </Select>
          </Space>
        ) : null}
        <Space direction="vertical">
          <label>Recherche :</label>
          <Tooltip
            title="La recherche ne s'applique qu'à partir de 3 caractères."
            visible={showSearchInfo}
          >
            <Input
              style={{ width: 400 }}
              suffix={<SearchOutlined />}
              placeholder="Tapez des caractères"
              allowClear={true}
              onChange={handleSearchValue}
            />
          </Tooltip>
        </Space>
      </Space>

      <Table
        mode={mode}
        pagination={pagination}
        sorter={sorter}
        loading={loading}
        surgicalInterventions={surgicalInterventions}
        fetch={fetch}
        onCancel={() => {}}
      />
    </div>
  );
});

export default SurgicalInterventionTable;
